import React from 'react';
import { useIntl } from 'react-intl';
import { MdAutoFixHigh, MdChatBubbleOutline, MdPublish } from 'react-icons/md';
import { Button, Modal, MODAL_SIZE_XL } from '@aibuild/ui';
import PromptDiff from './PromptDiff';
import { Findings, ReviewActions, ReviewBody } from './PublishReviewDialog.styled';

/** What the judge said instead of publishing: a security block, or advice the creator may publish over. */
export interface PublishReview {
  readonly kind: 'blocked' | 'advice';
  readonly findings: readonly string[];
  readonly currentPrompt: string;
  /** Set when the advice includes a rewritten prompt. */
  readonly revisedPrompt: string | null;
  readonly draftLockVersion: number;
}

interface PublishReviewDialogProps {
  readonly review: PublishReview | null;
  readonly applying: boolean;
  readonly publishing: boolean;
  readonly onApply: () => void;
  readonly onPublishAnyway: () => void;
  /** Hands the findings to the builder assistant to fix. */
  readonly onAskAssistant: (findings: readonly string[]) => void;
  readonly onClose: () => void;
}

const PublishReviewDialog: React.FC<PublishReviewDialogProps> = ({
  review,
  applying,
  publishing,
  onApply,
  onPublishAnyway,
  onAskAssistant,
  onClose,
}) => {
  const intl = useIntl();
  const busy = applying || publishing;
  const blocked = review?.kind === 'blocked';

  return (
    <Modal
      open={review !== null}
      onClose={onClose}
      size={MODAL_SIZE_XL}
      title={
        blocked
          ? intl.formatMessage({ id: 'apps.builder.review.blockedTitle', defaultMessage: 'This app cannot be published' })
          : intl.formatMessage({ id: 'apps.builder.review.title', defaultMessage: 'A suggestion before you publish' })
      }
      subtitle={
        blocked
          ? intl.formatMessage({
              id: 'apps.builder.review.blockedSubtitle',
              defaultMessage: 'The review found a security problem. Fix it, then publish again.',
            })
          : intl.formatMessage({
              id: 'apps.builder.review.subtitle',
              defaultMessage: 'This is only advice. Fix it and publish again, or publish the app as it is.',
            })
      }
      dataTestId={blocked ? 'app-builder__review-blocked' : 'app-builder__review'}
      footer={
        review && (
          <ReviewActions>
            {!blocked && (
              <Button
                variant="outlined"
                leadingIcon={<MdPublish size={16} />}
                loading={publishing}
                disabled={busy}
                onClick={onPublishAnyway}
                dataTestId="app-builder__review-publish-anyway"
              >
                {intl.formatMessage({ id: 'apps.builder.review.publishAnyway', defaultMessage: 'Publish anyway' })}
              </Button>
            )}
            <Button
              variant={review.revisedPrompt ? 'outlined' : 'filled'}
              leadingIcon={<MdChatBubbleOutline size={16} />}
              disabled={busy}
              onClick={() => onAskAssistant(review.findings)}
              dataTestId="app-builder__review-ask"
            >
              {intl.formatMessage({ id: 'apps.builder.review.ask', defaultMessage: 'Ask the assistant to fix' })}
            </Button>
            {review.revisedPrompt && (
              <Button
                variant="filled"
                leadingIcon={<MdAutoFixHigh size={16} />}
                loading={applying}
                disabled={busy}
                onClick={onApply}
                dataTestId="app-builder__review-apply"
              >
                {intl.formatMessage({ id: 'apps.builder.review.apply', defaultMessage: 'Apply suggestion' })}
              </Button>
            )}
          </ReviewActions>
        )
      }
    >
      {review && (
        <ReviewBody>
          {review.findings.length > 0 && (
            <Findings data-testid="app-builder__review-findings">
              {review.findings.map((finding) => (
                <li key={finding}>{finding}</li>
              ))}
            </Findings>
          )}
          {review.revisedPrompt && <PromptDiff before={review.currentPrompt} after={review.revisedPrompt} />}
        </ReviewBody>
      )}
    </Modal>
  );
};

export default PublishReviewDialog;
